import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import Dialog from "@mui/material/Dialog";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate } from "react-router";
import API from "../../../services";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft } from "@fortawesome/free-solid-svg-icons";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useTranslation } from "../../../hooks/useTranslation";
import { connect } from "react-redux";
import { RootState } from "../../../models/RootState";

interface CustomerSupportProps {
    dynamicTextColors?: any;
}

const TicketDetails: React.FC<CustomerSupportProps> = ({ dynamicTextColors }) => {
    const { t } = useTranslation();
    const pageTextColorClass = dynamicTextColors?.pageTextColorClass || "";
    const inputBoxTextColorClass = dynamicTextColors?.inputBoxTextColorClass || "";
    const buttonTextColorClass = dynamicTextColors?.buttonTextColorClass || "";
    const location = useLocation();
    const { details, ticketId } = location.state || {};
    const [newTicket, setNewTicket] = useState<any>(false);
    const [newTicDes, setNewTicDes] = useState<any>("");
    const navigate = useNavigate();
    const isFirstRender = useRef(true);
    const [ticDetails, setTicDetails] = useState<any>();
    const [selectedImages, setSelectedImages] = useState<File[]>([]);
    const [previews, setPreviews] = useState<string[]>([]);
    const [expImg, setExpImg] = useState<any>(false);
    const [selectedUrl, setSelectedUrl] = useState<any>("");
    const [loadSubmit, setLoadSubmit] = useState<any>(false);
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    const dateFormat = (time: any) => {
        const matchStartTime = new Date(time * 1000);

        const formattedStartTime = matchStartTime.toLocaleString("en-GB", {
            day: "numeric",
            month: "numeric",
            year: "numeric",
            hour: "numeric",
            minute: "numeric",
            second: "numeric",
            hour12: false,
        });

        return formattedStartTime
    }


    const handleBackClick = () => {
        navigate(-1);
    };

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {

        if (!e.target.files || e.target.files.length === 0) {
            console.log("No files selected");
            return;
        }

        const files = Array.from(e.target.files);
        console.log("Files selected:", files);

        const validFiles = files.filter(file => file.size < 1024 * 1024); // Ensure size < 1MB
        if (validFiles.length === 0) {
            toast.info(t("support.messages.onlyImagesLessThan1MBAllowed"));
            return;
        }

        setSelectedImages((prevImages) => [...prevImages, ...validFiles]);
        setPreviews((prevPreviews) => [
            ...prevPreviews,
            ...validFiles.map((file) => URL.createObjectURL(file)),
        ]);

        // Fix: Reinitialize file input to ensure event triggers on next selection
        resetFileInput();
    };

    const removeImage = (index: number) => {
        setSelectedImages((prevImages) => prevImages.filter((_, i) => i !== index));
        setPreviews((prevPreviews) => prevPreviews.filter((_, i) => i !== index));
    };

    const resetFileInput = () => {
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
            const newInput = fileInputRef.current.cloneNode(true) as HTMLInputElement;
            fileInputRef.current.replaceWith(newInput);
            fileInputRef.current = newInput;

            // Fix: Wrap handleImageChange inside an event listener conversion
            fileInputRef.current.addEventListener("change", (evt) => {
                handleImageChange(evt as unknown as React.ChangeEvent<HTMLInputElement>);
            });
        }
    };

    const getSupportTicket = async () => {
        // if (isFirstRender.current) {
        //     setLoading(true);
        //     isFirstRender.current = false;
        // }

        let response;
        try {
            response = await API.post(`/webapp/get-ticket`, {
                ticketId: ticketId
            });
        } catch (e) {
            console.log(e);
            // setLoading(false);
            return;
        }

        if (response.data.status !== "RS_OK") {
            navigate("/home");
        }

        if (response && response.data?.ticket) {
            setTicDetails(response?.data?.ticket)
        }
        // setLoading(false);
    };

    useEffect(() => {
        if (details) {
            setTicDetails(details)
        }

        if (ticketId) {
            getSupportTicket();
        }
    }, [details, ticketId]);

    const handleWithDrawSubmit = async (event: React.FormEvent) => {
        event.preventDefault();

        setLoadSubmit(true);

        const fileToBase64 = (file: File): Promise<string> => {
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => {
                    const result = reader.result as string;
                    const base64 = result.includes(",") ? result.split(",")[1] : result;
                    resolve(base64);
                };
                reader.onerror = (error) => reject(error);
                reader.readAsDataURL(file);
            });
        };

        try {
            const images = await Promise.all(selectedImages.map((image) => fileToBase64(image)));
            const image_names = selectedImages.map((image) => image.name);
            const image_types = selectedImages.map((image) => image.type);

            const payload = {
                comment: newTicDes,
                status: ticDetails?.Status,
                ticketId: ticDetails?.TicketId
            };

            const formData = new FormData();
            formData.append("messageData", JSON.stringify(payload));

            if (images.length > 0) {
                formData.append("image", images.join(","));
                formData.append("image_names", image_names.join(","));
                formData.append("image_types", image_types.join(","));
            }

            const response = await API.post(`/webapp/update-support-ticket`, formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            });

            const data = response.data;
            if (data?.status !== "RS_OK") {
                toast.error(`${data.errorDescription}`);
                return;
            } else {
                toast.success(t("support.messages.ticketUpdatedSuccessfully"));
                navigate("/customersupport");
            }
            setNewTicDes("");
            setNewTicket(false);

        } catch (error) {
            console.error("There was a problem with the fetch operation:", error);
        } finally {
            setLoadSubmit(false);
        }
    };

    return (
        <>
            <div className="flex flex-col px-3 mt-4 w-full pb-[7rem] py-[4rem] customer-support">
                <header
                    className={`flex gap-3 items-center self-start text-base font-medium text-center mb-[0.5rem] w-full justify-between ${pageTextColorClass} ${pageTextColorClass === "dark-text" ? "text-black" : "text-white"}`}
                    style={{ color: pageTextColorClass === "dark-text" ? "#000000" : "#FFFFFF" }}
                >
                    <div>
                        <div className="bg-inputbox-color flex p-1 mr-[0.25rem] rounded"
                            onClick={() => handleBackClick()}
                        >
                            <ArrowBackIcon
                                className={`aspect-square w-[18px] ${inputBoxTextColorClass} ${inputBoxTextColorClass === "dark-text" ? "text-black" : "text-white"}`}
                                style={{ width: "20px", height: "20px", color: inputBoxTextColorClass === "dark-text" ? "#000000" : "#FFFFFF" }}
                            />
                        </div>
                    </div>
                    <h2 className="self-stretch my-auto">{t("support.ticketDetails")}</h2>
                    <button
                        onClick={() => setNewTicket(true)}
                        className={`flex items-center justify-between px-3 py-2 text-xs font-medium whitespace-nowrap rounded-lg border border-solid bg-inputbox-color border-slate-800 ${buttonTextColorClass} ${buttonTextColorClass === "dark-text" ? "text-black" : "text-white"}`}
                        style={{ color: buttonTextColorClass === "dark-text" ? "#000000" : "#FFFFFF" }}
                    >
                        <span>{t("support.newComment")}</span>
                    </button>
                </header>
                <div className="p-2">
                    <div className="mb-4">
                        <p
                            className={`text-base font-medium ${pageTextColorClass} ${pageTextColorClass === "dark-text" ? "text-black" : "text-white"}`}
                            style={{ color: pageTextColorClass === "dark-text" ? "#000000" : "#FFFFFF" }}
                        >
                            {t("support.ticketId")}
                        </p>
                        <div className="p-1">
                            <p
                                className={`text-sm ${pageTextColorClass} ${pageTextColorClass === "dark-text" ? "text-black" : "text-white"}`}
                                style={{ color: pageTextColorClass === "dark-text" ? "#000000" : "#FFFFFF" }}
                            >
                                {ticDetails?.TicketId}
                            </p>
                        </div>
                    </div>

                    <div className="mb-4">
                        <p
                            className={`text-base font-medium ${pageTextColorClass} ${pageTextColorClass === "dark-text" ? "text-black" : "text-white"}`}
                            style={{ color: pageTextColorClass === "dark-text" ? "#000000" : "#FFFFFF" }}
                        >
                            {t("support.createdAt")}
                        </p>
                        <div className="p-1">
                            <p
                                className={`text-sm ${pageTextColorClass} ${pageTextColorClass === "dark-text" ? "text-black" : "text-white"}`}
                                style={{ color: pageTextColorClass === "dark-text" ? "#000000" : "#FFFFFF" }}
                            >
                                {dateFormat(ticDetails?.CreatedAt)}
                            </p>
                        </div>
                    </div>

                    <div className="mb-4">
                        <p
                            className={`text-base font-medium ${pageTextColorClass} ${pageTextColorClass === "dark-text" ? "text-black" : "text-white"}`}
                            style={{ color: pageTextColorClass === "dark-text" ? "#000000" : "#FFFFFF" }}
                        >
                            {t("support.description")}
                        </p>
                        <div className="p-1">
                            <p
                                className={`text-sm ${pageTextColorClass} ${pageTextColorClass === "dark-text" ? "text-black" : "text-white"}`}
                                style={{ color: pageTextColorClass === "dark-text" ? "#000000" : "#FFFFFF" }}
                            >
                                {ticDetails?.Description}
                            </p>
                        </div>
                        {ticDetails?.Attachments && ticDetails?.Attachments?.map((url: any, index: number) => (
                            <img key={index} src={url} alt={`attachment-${index}`} className="w-16 h-16 object-cover rounded-lg"
                                onClick={() => { setSelectedUrl(url); setExpImg(true) }}
                            />
                        ))}
                    </div>

                    {ticDetails?.ReferenceId && ticDetails?.ReferenceId !== "N/A" && (
                        <div className="mb-4">
                            <p
                                className={`text-base font-medium ${pageTextColorClass} ${pageTextColorClass === "dark-text" ? "text-black" : "text-white"}`}
                                style={{ color: pageTextColorClass === "dark-text" ? "#000000" : "#FFFFFF" }}
                            >
                                {t("support.referenceIdLabel")}
                            </p>
                            <div className="p-1">
                                <p
                                    className={`text-sm ${pageTextColorClass} ${pageTextColorClass === "dark-text" ? "text-black" : "text-white"}`}
                                    style={{ color: pageTextColorClass === "dark-text" ? "#000000" : "#FFFFFF" }}
                                >
                                    {ticDetails?.ReferenceId}
                                </p>
                            </div>
                        </div>
                    )}


                    <div className="mb-4">
                        <div
                            className={`text-base font-medium ${pageTextColorClass} ${pageTextColorClass === "dark-text" ? "text-black" : "text-white"}`}
                            style={{ color: pageTextColorClass === "dark-text" ? "#000000" : "#FFFFFF" }}
                        >
                            {t("bets.status")}:{" "}
                            <span className="text-sm font-normal">
                                {ticDetails?.Status}
                            </span>
                        </div>
                    </div>

                    <div className="mb-4">
                        <div
                            className={`text-base font-medium ${pageTextColorClass} ${pageTextColorClass === "dark-text" ? "text-black" : "text-white"}`}
                            style={{ color: pageTextColorClass === "dark-text" ? "#000000" : "#FFFFFF" }}
                        >
                            {t("bets.category")}:{" "}
                            <span className="text-sm font-normal">
                                {ticDetails?.Category}
                            </span>
                        </div>
                    </div>
                    {
                        (ticDetails?.Updates && ticDetails?.Updates?.length > 0) ? <>
                            <div className="mb-4">
                                <p
                                    className={`text-base font-medium ${pageTextColorClass} ${pageTextColorClass === "dark-text" ? "text-black" : "text-white"}`}
                                    style={{ color: pageTextColorClass === "dark-text" ? "#000000" : "#FFFFFF" }}
                                >
                                    {t("support.updates")}
                                </p>
                                <div className="p-1">
                                    {
                                        ticDetails?.Updates?.map((update: any) => (
                                            <div>
                                                <p
                                                    className={`text-sm ${pageTextColorClass} ${pageTextColorClass === "dark-text" ? "text-black" : "text-white"}`}
                                                    style={{ color: pageTextColorClass === "dark-text" ? "#000000" : "#FFFFFF" }}
                                                >
                                                    <strong>{update?.UpdatedBy}: </strong>
                                                    {update?.Comment}
                                                </p>
                                                <div className="grid grid-cols-4">
                                                    {update?.Attachments && update?.Attachments?.map((url: any, index: number) => (
                                                        <img key={index} src={url} alt={`attachment-${index}`} className="w-16 h-16 mb-2 rounded-lg"
                                                            onClick={() => { setSelectedUrl(url); setExpImg(true) }}
                                                        />
                                                    ))}
                                                </div>
                                            </div>
                                        ))
                                    }
                                </div>
                            </div>
                        </> : <></>
                    }
                </div>


            </div>

            {
                newTicket && (
                    <Dialog
                        fullScreen={false}
                        open={newTicket}
                        onClose={() => setNewTicket(false)}
                        PaperProps={{
                            style: {
                                position: 'absolute',
                                top: '5rem'
                            }
                        }}
                    >
                        <div className="h-dialog-bg bg-inputbox-color relative p-4">
                            <CloseIcon
                                className={`absolute top-3 right-3 text-2xl font-semibold ${pageTextColorClass} ${pageTextColorClass === "dark-text" ? "text-black" : "text-white"}`}
                                style={{ color: pageTextColorClass === "dark-text" ? "#000000" : "#FFFFFF" }}
                                onClick={() => setNewTicket(false)}
                            />
                            <div>
                                <div
                                    className={`${pageTextColorClass} ${pageTextColorClass === "dark-text" ? "text-black" : "text-white"}`}
                                    style={{ color: pageTextColorClass === "dark-text" ? "#000000" : "#FFFFFF" }}
                                >
                                    {t("support.addAComment")}
                                </div>
                                <div>
                                    <>
                                        <form className="flex flex-col w-full" onSubmit={handleWithDrawSubmit}>
                                            <label
                                                htmlFor="amount"
                                                className={`block mt-2 text-sm ${inputBoxTextColorClass} ${inputBoxTextColorClass === "dark-text" ? "text-gray-600" : "text-zinc-400"}`}
                                                style={{ color: inputBoxTextColorClass === "dark-text" ? "#666666" : "#A1A1AA" }}
                                            >
                                                {t("support.comment")}
                                            </label>
                                            <input
                                                type="text"
                                                className={`px-4 py-3 mt-2.5 w-full text-sm font-medium placeholder:text-text-color whitespace-nowrap rounded-lg border border-solid bg-inputbox-color border-border-color focus:outline-none leading-[1.4rem] ${inputBoxTextColorClass} ${inputBoxTextColorClass === "dark-text" ? "text-black" : "text-white"}`}
                                                style={{ color: inputBoxTextColorClass === "dark-text" ? "#000000" : "#FFFFFF" }}
                                                placeholder={t("support.enterYourIssue")}
                                                value={newTicDes || ""}
                                                onChange={(e) => setNewTicDes(e.target.value)}
                                            />

                                            <div>
                                                <label
                                                    className={`block mt-3 text-sm ${inputBoxTextColorClass} ${inputBoxTextColorClass === "dark-text" ? "text-gray-600" : "text-zinc-400"}`}
                                                    style={{ color: inputBoxTextColorClass === "dark-text" ? "#666666" : "#A1A1AA" }}
                                                >
                                                    {t("support.attachImage")}
                                                </label>
                                                <label className="block w-full mt-2 mb-2">
                                                    <input
                                                        id="file-upload"
                                                        ref={fileInputRef}
                                                        type="file"
                                                        accept="image/*"
                                                        // multiple
                                                        onChange={handleImageChange}
                                                        className="hidden"
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={() => fileInputRef.current?.click()}
                                                        className={`flex items-center justify-center w-full px-4 py-3 text-sm font-medium whitespace-nowrap rounded-lg border border-solid bg-inputbox-color border-border-color shadow-sm hover:bg-opacity-80 ${inputBoxTextColorClass} ${inputBoxTextColorClass === "dark-text" ? "text-black" : "text-white"}`}
                                                        style={{ color: inputBoxTextColorClass === "dark-text" ? "#000000" : "#FFFFFF" }}
                                                    >
                                                        {t("support.chooseImage")}
                                                    </button>
                                                </label>
                                                <label
                                                    className={`block text-xs ${inputBoxTextColorClass} ${inputBoxTextColorClass === "dark-text" ? "text-gray-600" : "text-zinc-400"}`}
                                                    style={{ color: inputBoxTextColorClass === "dark-text" ? "#666666" : "#A1A1AA" }}
                                                >
                                                    {t("support.onlyImagesLessThan1MB")}
                                                </label>
                                                <label
                                                    className={`block text-xs ${inputBoxTextColorClass} ${inputBoxTextColorClass === "dark-text" ? "text-gray-600" : "text-zinc-400"}`}
                                                    style={{ color: inputBoxTextColorClass === "dark-text" ? "#666666" : "#A1A1AA" }}
                                                >
                                                    {t("support.noteIfUnableToUpload")}
                                                </label>

                                                {previews.length > 0 && (
                                                    <div className="grid grid-cols-3 gap-4 mt-2">
                                                        {previews.map((preview, index) => (
                                                            <div key={index} className="relative group">
                                                                <img
                                                                    src={preview}
                                                                    alt={`Preview ${index}`}
                                                                    className="w-24 h-24 object-cover rounded-lg border border-border-color shadow-md"
                                                                />
                                                                <button
                                                                    onClick={() => removeImage(index)}
                                                                    className="absolute top-1 right-1 bg-red-600 text-white rounded-full w-5 h-5 text-xs flex items-center justify-center opacity-90 hover:opacity-100 transition"
                                                                >
                                                                    ✕
                                                                </button>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>

                                            <div className="flex justify-between mt-4">
                                                <button
                                                    type="submit"
                                                    className={`p-2.5 w-[48%] text-sm bg-border-color rounded-lg border border-border-color border-solid leading-[1.4rem] ${buttonTextColorClass} ${buttonTextColorClass === "dark-text" ? "text-black" : "text-white"}`}
                                                    style={{ color: buttonTextColorClass === "dark-text" ? "#000000" : "#FFFFFF" }}
                                                    disabled={!newTicDes || loadSubmit}
                                                >
                                                    {loadSubmit ? t("support.submitting") : t("common.submit")}
                                                </button>
                                                <button
                                                    type="button"
                                                    className={`p-2.5 w-[48%] text-sm bg-error-color rounded-lg border border-error-color border-solid leading-[1.4rem] ${buttonTextColorClass} ${buttonTextColorClass === "dark-text" ? "text-black" : "text-white"}`}
                                                    style={{ color: buttonTextColorClass === "dark-text" ? "#000000" : "#FFFFFF" }}
                                                    onClick={() => { setNewTicket(false) }}
                                                >
                                                    {t("common.cancel")}
                                                </button>
                                            </div>
                                        </form>
                                    </>
                                </div>
                            </div>
                        </div>
                    </Dialog>
                )
            }

            {
                expImg && (
                    <Dialog
                        fullScreen={false}
                        open={expImg}
                        onClose={() => setExpImg(false)}
                        PaperProps={{
                            style: {
                                position: 'absolute',
                                top: '5rem'
                            }
                        }}
                    >
                        <div className="img-dialog-bg bg-inputbox-color relative p-4">
                            <CloseIcon
                                className={`absolute top-3 right-3 text-2xl font-semibold ${pageTextColorClass} ${pageTextColorClass === "dark-text" ? "text-black" : "text-white"}`}
                                style={{ color: pageTextColorClass === "dark-text" ? "#000000" : "#FFFFFF" }}
                                onClick={() => setExpImg(false)}
                            />
                            <div>
                                <img
                                    src={selectedUrl}
                                    alt="img"
                                />
                            </div>
                        </div>
                    </Dialog>
                )
            }
        </>
    );
};

const mapStateToProps = (state: RootState) => ({
    dynamicTextColors: state.userDetails?.dynamicTextColors,
});

export default connect(mapStateToProps)(TicketDetails);